class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    /* The empty-state CSS keys off cart-drawer.is-empty — the element itself,
       not .drawer__inner — so adding to an EMPTY cart left that class behind:
       the drawer opened with its header hidden and the new line at zero
       height, showing a blank panel above a correct subtotal. cart.js already
       does this on quantity updates; the add path was missing it.
       (add.js returns no item_count, and an add always leaves the cart
       non-empty, so undefined correctly reads as "not empty".) */
    const cartIsEmpty = parsedState.item_count === 0;
    this.classList.toggle('is-empty', cartIsEmpty);
    const drawerFooter = document.getElementById('CartDrawer-Footer');
    if (drawerFooter) drawerFooter.classList.toggle('is-empty', cartIsEmpty);
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* ---------------------------------------------------------------------------
   Cart drawer extras: upsell Add buttons and the recommendations rail.

   Shopify re-renders the whole drawer on every cart change, so the progress
   bar and the savings figures in snippets/cart-extras.liquid look after
   themselves. Two things cannot be done in Liquid and live here instead:

     1. The Add buttons on upsell cards. They are plain buttons because the
        line items already sit inside a form and nested forms are invalid.
     2. The recommendations rail, because Shopify only exposes recommendations
        over HTTP, not to Liquid.
   --------------------------------------------------------------------------- */

function ttCartFormatMoney(cents, format) {
  const value = Number(cents) || 0;
  const pattern = format || '${{amount}}';
  const withDecimals = (value / 100).toFixed(2);
  const noDecimals = String(Math.round(value / 100));
  const comma = (n) => n.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const [whole, centsPart] = withDecimals.split('.');

  return pattern.replace(/\{\{\s*(\w+)\s*\}\}/, (match, token) => {
    switch (token) {
      case 'amount_no_decimals':
        return comma(noDecimals);
      case 'amount_with_comma_separator':
        return comma(whole).replace(/,/g, '.') + ',' + centsPart;
      case 'amount_no_decimals_with_comma_separator':
        return comma(noDecimals).replace(/,/g, '.');
      case 'amount_with_apostrophe_separator':
        return comma(whole).replace(/,/g, "'") + '.' + centsPart;
      default:
        return comma(whole) + '.' + centsPart;
    }
  });
}

/* Add one variant to the cart and hand the fresh markup to the drawer, the
   same way the standard Add to cart does. Falls back to the cart page if the
   drawer is missing or its API has changed. */
function ttCartAddVariant(variantId, button) {
  const base = (window.routes && window.routes.cart_add_url) || '/cart/add';
  const url = base.indexOf('.js') === -1 ? base + '.js' : base;
  const drawer = document.querySelector('cart-drawer');
  const drawerUsable =
    drawer &&
    typeof drawer.renderContents === 'function' &&
    typeof drawer.getSectionsToRender === 'function';

  const payload = { items: [{ id: Number(variantId), quantity: 1 }] };
  if (drawerUsable) {
    payload.sections = drawer.getSectionsToRender().map((section) => section.id);
    payload.sections_url = window.location.pathname;
  }

  if (button) {
    button.disabled = true;
    button.dataset.label = button.textContent;
    button.textContent = 'Adding';
  }

  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  })
    .then((response) => response.json().then((body) => ({ ok: response.ok, body })))
    .then((result) => {
      if (!result.ok) throw new Error('add failed');

      if (drawerUsable && payload.sections) {
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-upsell',
            productVariantId: variantId,
            cartData: result.body,
          });
        }
        drawer.renderContents(result.body);
        return;
      }

      window.location.assign((window.routes && window.routes.cart_url) || '/cart');
    })
    .catch(() => {
      if (button) {
        button.disabled = false;
        button.textContent = button.dataset.label || 'Add';
        button.classList.add('tt-cart-card__add--error');
      }
    });
}

/* Delegated on the document so it survives the drawer being re-rendered. */
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-cart-add]');
  if (!button || button.disabled) return;
  event.preventDefault();
  ttCartAddVariant(button.dataset.variantId, button);
});

class CartRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.loaded) return;
    this.loaded = true;

    const productId = this.dataset.productId;
    if (!productId) return;

    /* Complementary products are the ones merchandised by hand in the free
       Search and Discovery app. When none are set the endpoint returns an
       empty list rather than an error, so fall back to the automatic related
       list and the rail is never empty. */
    this.fetchIntent(productId, 'complementary')
      .then((products) =>
        products.length ? products : this.fetchIntent(productId, 'related')
      )
      .then((products) => this.render(products))
      .catch(() => this.remove());
  }

  fetchIntent(productId, intent) {
    const limit = Math.min(Number(this.dataset.limit) || 3, 10) + 4;
    const url = `/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=${intent}`;

    return fetch(url)
      .then((response) => (response.ok ? response.json() : { products: [] }))
      .then((data) => this.filter(data.products || []));
  }

  /* Drop anything already in the cart, anything sold out, and anything the
     companion's curated block is already showing. */
  filter(products) {
    let exclude = this.dataset.exclude || ',';
    const companion = this.closest('.tt-cart-upsell--companion');
    if (companion) {
      companion.querySelectorAll('[data-curated-id]').forEach((card) => {
        exclude += `${card.dataset.curatedId},`;
      });
    }
    return products.filter(
      (product) => product.available && exclude.indexOf(`,${product.id},`) === -1
    );
  }

  render(products) {
    const limit = Number(this.dataset.limit) || 3;
    const shown = products.slice(0, limit);
    if (!shown.length) {
      this.remove();
      return;
    }

    const heading = this.dataset.heading || 'You may also like';
    const cards = shown.map((product) => this.card(product)).filter(Boolean);
    if (!cards.length) {
      this.remove();
      return;
    }

    this.innerHTML =
      `<h3 class="tt-cart-upsell__heading">${heading}</h3>` +
      `<div class="tt-cart-upsell__list">${cards.join('')}</div>`;
  }

  card(product) {
    const variant = (product.variants || []).find((candidate) => candidate.available);
    if (!variant) return null;

    const format = this.dataset.moneyFormat;
    const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    const savePct = onSale ? Math.round((1 - variant.price / variant.compare_at_price) * 100) : 0;
    const image = product.featured_image
      ? `<img src="${product.featured_image}&width=160" width="80" height="80" alt="" loading="lazy">`
      : '';

    return `
      <div class="tt-cart-card">
        <a class="tt-cart-card__media" href="${product.url}" tabindex="-1" aria-hidden="true">${image}</a>
        <div class="tt-cart-card__body">
          <a class="tt-cart-card__title" href="${product.url}">${product.title}</a>
          ${savePct > 0 ? `<span class="tt-cart-card__chip">Save ${savePct}%</span>` : ''}
          <div class="tt-cart-card__meta">
            <span class="tt-cart-card__price">
              ${onSale ? `<s class="tt-cart-card__was">${ttCartFormatMoney(variant.compare_at_price, format)}</s>` : ''}
              <span${onSale ? ' class="tt-cart-card__now"' : ''}>${ttCartFormatMoney(variant.price, format)}</span>
            </span>
            <button type="button" class="tt-cart-card__add" data-cart-add data-variant-id="${variant.id}">Add</button>
          </div>
        </div>
      </div>`;
  }
}

customElements.define('cart-recommendations', CartRecommendations);
